import { Box, makeStyles, Popover } from "@material-ui/core";
import { ComparableProperty } from "../../types";
import { memo, useMemo, useRef, useState } from "react";
import { observer } from "mobx-react";
import { CompPropertyThumbnail } from "./compPropertyThumbnail";

import { ReactComponent as ListedTargetMarkerSVG } from "../../images/listedTargetMarker.svg";
import { ReactComponent as ListedTargetSelectedMarkerSVG } from "../../images/listedTargetSelectedMarker.svg";
import { ReactComponent as ListedTargetMultipleDigitsMarkerSVG } from "../../images/listedTargetMultipleDigitsMarker.svg";
import { ReactComponent as ListedTargetMultipleDigitsSelectedMarkerSVG } from "../../images/listedTargetMultipleDigitsSelectedMarker.svg";

import { ReactComponent as AppraisalMarkerSVG } from "../../images/appraisalMarker.svg";
import { ReactComponent as AppraisalSelectedMarkerSVG } from "../../images/appraisalSelectedMarker.svg";
import { ReactComponent as AppraisalMultipleDigitsMarkerSVG } from "../../images/appraisalMultipleDigitsMarker.svg";
import { ReactComponent as AppraisalMultipleDigitsSelectedMarkerSVG } from "../../images/appraisalMultipleDigitsSelectedMarker.svg";
import { ReactComponent as DistressedMarkerSVG } from "../../images/distressedMarker.svg";
import { ReactComponent as DistressedMultipleDigitsMarkerSVG } from "../../images/distressedMultipleDigitsMarker.svg";

interface StyleProps {
  hasThreeDigitNumber: boolean;
  rightPosition: string;
}

const useStyles = makeStyles(() => ({
  numberContainer: ({ hasThreeDigitNumber, rightPosition }: StyleProps) => ({
    position: "absolute",
    top: hasThreeDigitNumber ? "5px" : "4px",
    right: rightPosition,
    color: "gray",
    width: "30px",
    height: "30px",
    fontWeight: "bold",
    fontSize: "13px",
    borderRadius: "100px",
    textAlign: "center",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    cursor: "pointer"
  })
}));
interface CompPropertyMarkerProps {
  property: ComparableProperty,
  onThumbnailClick?: () => void;
  isSelected: boolean;
}

export const CompPropertyMarker = observer(({ property, onThumbnailClick, isSelected }: CompPropertyMarkerProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const markerRef = useRef(null);

  const handleClick = () => {
    setAnchorEl(markerRef.current);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleThumbnailClick = () => {
    setAnchorEl(null);
    onThumbnailClick && onThumbnailClick();
  }

  const open = Boolean(anchorEl);
  const id = `simple-popover-${property?.property_id}`;

  const number = property.number;
  const hasThreeDigitNumber = property.number > 99;
  const inAppraisal = property?.data?.in_appraisal;
  const isDistressed = property?.data?.is_distressed;

  let rightPosition = "8px";
  if (inAppraisal) {
    if (hasThreeDigitNumber) {
      rightPosition = "9px";
    } else {
      rightPosition = "12px";
    }
  }

  const classes = useStyles({ rightPosition, hasThreeDigitNumber });

  return (
    <div ref={markerRef} style={{ position: "relative" }}>
      <MarkerIcon
        number={number}
        isSelected={isSelected}
        inAppraisal={inAppraisal}
        isDistressed={isDistressed}
        width={46}
        height={57}
        onClick={handleClick} />
      <Box
        className={classes.numberContainer}
        onClick={handleClick}
      >
        {property.number}
      </Box>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        <CompPropertyThumbnail property={property} onClick={handleThumbnailClick} />
      </Popover>
    </div>
  );
});

//Created this map to avoid a bunch of nested if's or switch conditions
function useMarkerMap() {
  return useMemo(() => {
    const map = new Map();

    // number > 99
    map.set("number-higher-than-99-true-is-selected-true-in-appraisal-true-is-distressed-true", AppraisalMultipleDigitsSelectedMarkerSVG);
    map.set("number-higher-than-99-true-is-selected-true-in-appraisal-true-is-distressed-false", AppraisalMultipleDigitsSelectedMarkerSVG);
    map.set("number-higher-than-99-true-is-selected-true-in-appraisal-false-is-distressed-true", ListedTargetMultipleDigitsSelectedMarkerSVG);
    map.set("number-higher-than-99-true-is-selected-true-in-appraisal-false-is-distressed-false", ListedTargetMultipleDigitsSelectedMarkerSVG);

    map.set("number-higher-than-99-true-is-selected-false-in-appraisal-true-is-distressed-true", AppraisalMultipleDigitsMarkerSVG);
    map.set("number-higher-than-99-true-is-selected-false-in-appraisal-true-is-distressed-false", AppraisalMultipleDigitsMarkerSVG);
    map.set("number-higher-than-99-true-is-selected-false-in-appraisal-false-is-distressed-true", DistressedMultipleDigitsMarkerSVG);
    map.set("number-higher-than-99-true-is-selected-false-in-appraisal-false-is-distressed-false", ListedTargetMultipleDigitsMarkerSVG);

    // number <= 99
    map.set("number-higher-than-99-false-is-selected-true-in-appraisal-true-is-distressed-true", AppraisalSelectedMarkerSVG);
    map.set("number-higher-than-99-false-is-selected-true-in-appraisal-true-is-distressed-false", AppraisalSelectedMarkerSVG);
    map.set("number-higher-than-99-false-is-selected-true-in-appraisal-false-is-distressed-true", ListedTargetSelectedMarkerSVG);
    map.set("number-higher-than-99-false-is-selected-true-in-appraisal-false-is-distressed-false", ListedTargetSelectedMarkerSVG);

    map.set("number-higher-than-99-false-is-selected-false-in-appraisal-true-is-distressed-true", AppraisalMarkerSVG);
    map.set("number-higher-than-99-false-is-selected-false-in-appraisal-true-is-distressed-false", AppraisalMarkerSVG);
    map.set("number-higher-than-99-false-is-selected-false-in-appraisal-false-is-distressed-true", DistressedMarkerSVG);
    map.set("number-higher-than-99-false-is-selected-false-in-appraisal-false-is-distressed-false", ListedTargetMarkerSVG);

    return map;
  }, []);
}

// memoized component
const MarkerIcon = memo(function MarkerIcon({
  number,
  isSelected,
  inAppraisal,
  isDistressed,
  width = 46,
  height = 57,
  onClick = () => {/**nothing to do here */ }
}: {
  number: number;
  isSelected: boolean;
  inAppraisal: boolean;
  isDistressed: boolean;
  width?: number;
  height?: number;
  onClick: () => void
}) {
  const markerMap = useMarkerMap();

  const key = `number-higher-than-99-${Boolean(number > 99)}-is-selected-${Boolean(isSelected)}-in-appraisal-${Boolean(inAppraisal)}-is-distressed-${Boolean(isDistressed)}`;
  const Icon = markerMap.get(key);

  return Icon ? <Icon width={width} height={height} onClick={onClick} style={{ cursor: "pointer" }} /> : null;
});